<template>
  <ImageRatioModal ref="imageRatioModal"
                   :applying="isApplyingCrop"
                   :image-edit-preview-url="activePreviewUrl"
                   :image-natural-height="imageNaturalHeight"
                   :image-natural-width="imageNaturalWidth"
                   :visible="isImageRatioModalVisible"
                   @apply="applySelectedAspect"
                   @close="cancelImageEditing"
                   @use-original="useOriginalImage"
  />
</template>

<script>
import ImageRatioModal from "@/view/User/Wall/components/ImageRatioModal.vue";
import { cropImageByAspect, IMAGE_ASPECT_RATIOS } from "@/utils/imageCrop";

export default {
  name: "SocialWallImageRatioEditor",
  components: {
    ImageRatioModal,
  },
  data() {
    return {
      pendingImageFiles: [],
      activeImageFile: null,
      activePreviewUrl: null,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      isApplyingCrop: false,
      isImageRatioModalVisible: false,
      editSessionId: 0,
    };
  },
  beforeDestroy() {
    this.resetEditor(false);
  },
  methods: {
    isEditingImages() {
      return Boolean(
          this.activeImageFile ||
          this.pendingImageFiles.length ||
          this.isApplyingCrop
      );
    },
    getPendingSourceFiles() {
      return [this.activeImageFile, ...this.pendingImageFiles].filter(Boolean);
    },
    enqueueImages(imageFiles) {
      const validImageFiles = (
          Array.isArray(imageFiles) ? imageFiles : []
      ).filter(Boolean);
      if (!validImageFiles.length) return;

      const wasAlreadyEditing = this.isEditingImages();
      this.pendingImageFiles = [...this.pendingImageFiles, ...validImageFiles];
      if (!wasAlreadyEditing) this.emitProcessingState();
      this.openNextImage();
    },
    emitProcessingState() {
      this.$emit("processing-change", this.isEditingImages());
    },
    emitEditorError(message) {
      this.$emit("error", message);
    },
    createPreviewUrl(file) {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      return urlApi?.createObjectURL ? urlApi.createObjectURL(file) : "";
    },
    releaseActivePreviewUrl() {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      if (this.activePreviewUrl && urlApi?.revokeObjectURL) {
        urlApi.revokeObjectURL(this.activePreviewUrl);
      }
      this.activePreviewUrl = null;
    },
    resetActiveImage() {
      this.editSessionId += 1;
      this.activeImageFile = null;
      this.imageNaturalWidth = 0;
      this.imageNaturalHeight = 0;
      this.releaseActivePreviewUrl();
      this.isApplyingCrop = false;
      this.$refs.imageRatioModal?.reset();
    },
    resetEditor(emitStateChange = true) {
      this.pendingImageFiles = [];
      this.resetActiveImage();
      this.isImageRatioModalVisible = false;
      if (emitStateChange) this.emitProcessingState();
    },
    loadImageDimensions(imageSourceUrl) {
      const ImageConstructor =
          typeof window !== "undefined" ? window.Image : null;
      if (!imageSourceUrl || !ImageConstructor) return;

      const image = new ImageConstructor();
      image.onload = () => {
        if (this.activePreviewUrl !== imageSourceUrl) return;
        this.imageNaturalWidth = image.naturalWidth || image.width || 0;
        this.imageNaturalHeight = image.naturalHeight || image.height || 0;
      };
      image.onerror = () => {
        if (this.activePreviewUrl !== imageSourceUrl) return;
        this.imageNaturalWidth = 0;
        this.imageNaturalHeight = 0;
      };
      image.src = imageSourceUrl;
    },
    openNextImage() {
      if (this.activeImageFile || !this.pendingImageFiles.length) return;

      const imageFile = this.pendingImageFiles.shift();
      const generatedPreviewUrl = this.createPreviewUrl(imageFile);
      if (!generatedPreviewUrl) {
        this.emitEditorError(this.$t('general.image_preview_failed'));
        if (this.pendingImageFiles.length) {
          this.openNextImage();
        } else {
          this.emitProcessingState();
        }
        return;
      }

      this.editSessionId += 1;
      this.activeImageFile = imageFile;
      this.activePreviewUrl = generatedPreviewUrl;
      this.imageNaturalWidth = 0;
      this.imageNaturalHeight = 0;
      this.loadImageDimensions(generatedPreviewUrl);

      this.$nextTick(() => {
        if (this.activeImageFile !== imageFile) return;
        this.$refs.imageRatioModal?.reset();
        this.isImageRatioModalVisible = true;
      });
    },
    cancelImageEditing() {
      this.pendingImageFiles = [];
      this.resetActiveImage();
      this.isImageRatioModalVisible = false;
      this.emitProcessingState();
    },
    completeActiveImage(file, sourceFile) {
      this.$emit("file-ready", { file, sourceFile });
      this.resetActiveImage();

      if (this.pendingImageFiles.length) {
        this.openNextImage();
      } else {
        this.isImageRatioModalVisible = false;
        this.emitProcessingState();
      }
    },
    useOriginalImage() {
      if (!this.activeImageFile || this.isApplyingCrop) return;
      this.completeActiveImage(this.activeImageFile, this.activeImageFile);
    },
    async applySelectedAspect(aspectSelection) {
      if (!this.activeImageFile || this.isApplyingCrop) return;

      const sourceFile = this.activeImageFile;
      const editSessionId = this.editSessionId;
      const selectedAspectRatio =
          IMAGE_ASPECT_RATIOS[aspectSelection?.selectedAspect];
      if (!selectedAspectRatio) {
        this.completeActiveImage(sourceFile, sourceFile);
        return;
      }

      this.isApplyingCrop = true;
      let outputFile = sourceFile;
      try {
        outputFile =
            (await cropImageByAspect(
                sourceFile,
                selectedAspectRatio,
                aspectSelection?.anchorX,
                aspectSelection?.anchorY
            )) || sourceFile;
      } catch {
        outputFile = sourceFile;
      }

      if (
          editSessionId !== this.editSessionId ||
          this.activeImageFile !== sourceFile
      ) {
        return;
      }
      this.completeActiveImage(outputFile, sourceFile);
    },
  },
};
</script>
